// Tournament editor pieces (contracts/tournaments.md §1, §6): the brackets editor, the prizes with per-language
// labels and their one-click translation and a gift-card picker, the UTC schedule with a local-time preview, and a
// country-codes field, and the labelled checkboxes for who sees tournaments. Shared by the cup editor, the cup page
// and the Weekly Cup template. Cup puzzles: a set's difficulty (mean ± spread on 0–1, the pool's target marked) and
// its type chips.

import { type ReactNode, useState } from "react";
import type { AdminCupListItem, AdminPrize } from "../../api/cups";
import { type Locale, LOCALE_LABEL, LOCALES, TRANSLATE_TARGETS, useTranslate } from "../../api/messaging";
import { useCan } from "../../auth/AuthContext";
import { Button, Field, Select, TextInput } from "../../components/ui";
import {
  type Bracket,
  bracketProblems,
  CUP_BADGES,
  CUP_LIMITS,
  DEFAULT_BRACKETS,
  DEFAULT_VALUE,
  type EditPrize,
  flag,
  GIFT_CARD_LABEL,
  GIFT_CARDS,
  hoursBetween,
  inZone,
  isCountry,
  KIND_LABEL,
  MAX_BRACKETS,
  MAX_PRIZES,
  MONEY_KINDS,
  POOLS,
  PRIZE_KIND_LABEL,
  PRIZE_KINDS,
  type PrizeKind,
  prizeProblems,
  PREVIEW_ZONES,
} from "../../lib/cups";
import { formatDateTime } from "../../lib/dates";
import { typeSlug } from "../../lib/format";
import { LocaleTabs, TranslateError } from "../messaging/components";
import m from "../messaging/messaging.module.css";
import styles from "../pages.module.css";
import c from "./cups.module.css";

// ---------------------------------------------------------------- cup helpers

/** The API's English title (a Weekly Cup's `{number}` filled in), else "Weekly Cup #12" / "Special cup". */
export const cupTitle = (c: Pick<AdminCupListItem, "title" | "content" | "kind" | "number">) =>
  c.title.trim() || c.content.en?.title?.trim() || (c.number ? `${KIND_LABEL[c.kind]} #${c.number}` : KIND_LABEL[c.kind]);

export function cupWhen(c: Pick<AdminCupListItem, "status" | "opensAt" | "closesAt">): string {
  if (c.status === "draft") return `draft · would open ${formatDateTime(c.opensAt)}`;
  if (c.status === "scheduled") return `opens ${formatDateTime(c.opensAt)}`;
  if (c.status === "open") return `closes ${formatDateTime(c.closesAt)}`;
  if (c.status === "closed") return `closed ${formatDateTime(c.closesAt)} · finalizing`;
  if (c.status === "finalized") return `closed ${formatDateTime(c.closesAt)}`;
  return `was to open ${formatDateTime(c.opensAt)}`;
}

export const hasMoneyPrizes = (prizes: readonly Pick<AdminPrize, "kind">[]) => prizes.some((p) => MONEY_KINDS.has(p.kind));

/** A checkbox with its title and a one-line explanation ("Hide tournaments in the app", "Hidden from players"). */
export function CheckRow({ title, hint, checked, onChange, disabled, testId }: { title: string; hint?: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; testId: string }) {
  return (
    <label className={c.checkRow}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      <span className={c.checkText}>
        <strong>{title}</strong>
        {hint ? <span>{hint}</span> : null}
      </span>
    </label>
  );
}

const num = (v: string) => (v.trim() === "" ? Number.NaN : Number(v));
const show = (n: number | null | undefined) => (n === null || n === undefined || Number.isNaN(n) ? "" : String(n));

// ---------------------------------------------------------------- brackets

export function BracketsEditor({ value, onChange }: { value: Bracket[]; onChange: (v: Bracket[]) => void }) {
  const problems = bracketProblems(value);
  const set = (i: number, patch: Partial<Bracket>) => onChange(value.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  return (
    <div className={c.editorBlock} data-testid="brackets">
      <table className={c.grid}>
        <colgroup>
          <col style={{ width: "21%" }} />
          <col style={{ width: "21%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "13%" }} />
          <col />
          <col style={{ width: 40 }} />
        </colgroup>
        <thead>
          <tr>
            <th>Bracket id</th>
            <th title="Shown to players for a custom bracket; rookie, challenger and master are translated by the server">Name · optional</th>
            <th>From level</th>
            <th>To level</th>
            <th>Puzzle pool</th>
            <th aria-label="Remove" />
          </tr>
        </thead>
        <tbody>
          {value.map((b, i) => (
            <tr key={i}>
              <td>
                <TextInput value={b.id} onChange={(e) => set(i, { id: e.target.value })} aria-label={`Bracket ${i + 1} id`} data-testid="bracket-id" />
              </td>
              <td>
                <TextInput
                  value={b.name ?? ""}
                  maxLength={CUP_LIMITS.bracketName}
                  placeholder={["rookie", "challenger", "master"].includes(b.id) ? "translated" : b.id}
                  onChange={(e) => set(i, { name: e.target.value.trim() ? e.target.value : undefined })}
                  aria-label={`Bracket ${i + 1} name`}
                />
              </td>
              <td>
                <TextInput type="number" min={1} value={show(b.minLevel)} onChange={(e) => set(i, { minLevel: num(e.target.value) })} aria-label={`Bracket ${i + 1} lowest level`} data-testid="bracket-min" />
              </td>
              <td>
                <TextInput
                  type="number"
                  min={1}
                  value={show(b.maxLevel)}
                  placeholder="no top"
                  onChange={(e) => set(i, { maxLevel: e.target.value.trim() === "" ? null : num(e.target.value) })}
                  aria-label={`Bracket ${i + 1} highest level`}
                  data-testid="bracket-max"
                />
              </td>
              <td>
                <Select value={b.pool} onChange={(e) => set(i, { pool: e.target.value })} aria-label={`Bracket ${i + 1} pool`}>
                  {[...new Set([...POOLS, b.pool])].filter(Boolean).map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Select>
              </td>
              <td>
                <Button size="sm" variant="ghost" disabled={value.length <= 1} onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label={`Remove bracket ${i + 1}`}>
                  ×
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={c.rowActions}>
        <Button
          size="sm"
          variant="ghost"
          disabled={value.length >= MAX_BRACKETS}
          onClick={() => {
            const last = value[value.length - 1];
            const from = last ? (last.maxLevel ?? last.minLevel) + 1 : 1;
            onChange([...value, { id: "", minLevel: from, maxLevel: null, pool: "cup.master" }]);
          }}
        >
          + Bracket
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onChange(DEFAULT_BRACKETS)}>
          Use the defaults
        </Button>
        <span className={styles.faint}>A player enters the bracket of their XP level, and may play up but never down. Levels between brackets fall into the lower one.</span>
      </div>
      {problems.length ? (
        <ul className={c.problems} data-testid="bracket-problems">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- prizes

const blankPrize = (bracket: string): EditPrize => ({ bracket, country: null, rankFrom: 1, rankTo: 1, kind: "xp", value: DEFAULT_VALUE.xp, content: { en: { label: "" } }, giftCards: [], minEntrants: 10 });
/** /translate takes at most 40 fields a call. */
const TRANSLATE_BATCH = 40;

export function PrizesEditor({
  value,
  onChange,
  brackets,
  locale,
  onLocale,
  max = MAX_PRIZES,
}: {
  value: EditPrize[];
  onChange: (v: EditPrize[]) => void;
  brackets: Bracket[];
  locale: Locale;
  onLocale: (l: Locale) => void;
  /** 60 on a cup, 40 in the Weekly template. */
  max?: number;
}) {
  const canTranslate = useCan("content.translate");
  const translate = useTranslate();
  const [translating, setTranslating] = useState(false);
  const problems = prizeProblems(value, brackets, max);
  const set = (i: number, patch: Partial<EditPrize>) => onChange(value.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const setLabel = (i: number, label: string) => set(i, { content: { ...value[i]!.content, [locale]: { label } } });
  const stateOf = (l: Locale) => {
    const withEn = value.filter((p) => p.content.en?.label?.trim());
    if (!withEn.length) return "empty" as const;
    const done = withEn.filter((p) => p.content[l]?.label?.trim()).length;
    return done === 0 ? ("empty" as const) : done === withEn.length ? ("complete" as const) : ("partial" as const);
  };
  /** Every English label (`p<i>label`, ≤ 60), 40 a call, folded back per prize. */
  const translateAll = async () => {
    const en = value.map((p, i) => [`p${i}label`, p.content.en?.label?.trim() ?? ""] as const).filter(([, v]) => v);
    if (!en.length) return;
    setTranslating(true);
    try {
      const got: Partial<Record<Locale, Record<string, string>>> = {};
      for (let k = 0; k < en.length; k += TRANSLATE_BATCH) {
        const batch = Object.fromEntries(en.slice(k, k + TRANSLATE_BATCH));
        const r = await translate.mutateAsync({ content: { en: batch }, limits: Object.fromEntries(Object.keys(batch).map((f) => [f, CUP_LIMITS.label])) });
        for (const l of LOCALES) if (r.content[l]) got[l] = { ...got[l], ...r.content[l] };
      }
      onChange(
        value.map((p, i) => {
          const content = { ...p.content };
          for (const l of LOCALES) {
            const t = got[l]?.[`p${i}label`];
            if (l !== "en" && t) content[l] = { label: t };
          }
          return { ...p, content };
        }),
      );
    } catch {
      // shown by TranslateError
    } finally {
      setTranslating(false);
    }
  };
  return (
    <div className={c.editorBlock} data-testid="prizes">
      <div className={m.localeBar}>
        <LocaleTabs locale={locale} onLocale={onLocale} stateOf={stateOf} label="Prize label languages" testPrefix="prize-locale" />
        <div className={m.translateRow}>
          {canTranslate ? (
            <Button size="sm" variant="secondary" busy={translating} disabled={!value.some((p) => p.content.en?.label?.trim())} onClick={() => void translateAll()} data-testid="translate-prizes">
              Translate prize labels
            </Button>
          ) : null}
          <span className={m.translateMeta}>
            Labels in {LOCALE_LABEL[locale]} · {TRANSLATE_TARGETS.filter((l) => stateOf(l) !== "empty").length} of {TRANSLATE_TARGETS.length} languages
          </span>
        </div>
      </div>
      <TranslateError error={translate.error} />
      {value.length ? (
        <div className={c.prizeHead} aria-hidden>
          <span>Bracket</span>
          <span>Country</span>
          <span>Ranks</span>
          <span>Kind</span>
          <span>Value</span>
          <span />
        </div>
      ) : null}
      <ol className={c.prizeRows}>
        {value.map((p, i) => (
          <li key={i} data-testid="prize-row">
            <Select value={p.bracket} onChange={(e) => set(i, { bracket: e.target.value })} aria-label={`Prize ${i + 1} bracket`}>
              {[...new Set([...brackets.map((b) => b.id), p.bracket])].filter(Boolean).map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </Select>
            <TextInput
              value={p.country ?? ""}
              maxLength={2}
              placeholder="all"
              onChange={(e) => set(i, { country: e.target.value.trim() ? e.target.value.toUpperCase() : null })}
              aria-label={`Prize ${i + 1} country`}
            />
            <span className={c.ranks}>
              <TextInput type="number" min={1} value={show(p.rankFrom)} onChange={(e) => set(i, { rankFrom: num(e.target.value) })} aria-label={`Prize ${i + 1} from rank`} />
              <span>–</span>
              <TextInput type="number" min={1} value={show(p.rankTo)} onChange={(e) => set(i, { rankTo: num(e.target.value) })} aria-label={`Prize ${i + 1} to rank`} />
            </span>
            <Select
              value={p.kind}
              onChange={(e) => {
                const kind = e.target.value as PrizeKind;
                set(i, { kind, value: DEFAULT_VALUE[kind], giftCards: kind === "gift_card" ? [...GIFT_CARDS] : [] });
              }}
              aria-label={`Prize ${i + 1} kind`}
              data-testid="prize-kind"
            >
              {PRIZE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {PRIZE_KIND_LABEL[k]}
                </option>
              ))}
            </Select>
            {p.kind === "badge" ? (
              <Select value={p.value} onChange={(e) => set(i, { value: e.target.value })} aria-label={`Prize ${i + 1} badge`}>
                {[...new Set([...CUP_BADGES, p.value])].map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </Select>
            ) : (
              <TextInput
                value={p.value}
                inputMode={p.kind === "xp" || p.kind === "pro_days" ? "numeric" : undefined}
                maxLength={CUP_LIMITS.value}
                placeholder={p.kind === "xp" ? "300" : p.kind === "pro_days" ? "days" : p.kind === "gift_card" ? "50" : "USD 50"}
                title={p.kind === "xp" ? "XP, 1–5000" : p.kind === "pro_days" ? "Days of Pro, 1–365" : "What it's worth, e.g. 50 or USD 50 (for the team)"}
                onChange={(e) => set(i, { value: e.target.value })}
                aria-label={`Prize ${i + 1} value`}
                data-testid="prize-value"
              />
            )}
            <Button size="sm" variant="ghost" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label={`Remove prize ${i + 1}`}>
              ×
            </Button>
            <label className={c.prizeLabel}>
              <span className="monoLabel">Label · {locale}</span>
              <TextInput
                value={p.content[locale]?.label ?? ""}
                maxLength={CUP_LIMITS.label}
                placeholder={locale === "en" ? "e.g. $50 Amazon or Google Play gift card" : (p.content.en?.label ?? "")}
                onChange={(e) => setLabel(i, e.target.value)}
                aria-label={`Prize ${i + 1} label`}
                data-testid="prize-label"
              />
            </label>
            <label className={c.prizeMin}>
              <span className="monoLabel">Min entrants</span>
              <TextInput type="number" min={1} value={show(p.minEntrants)} onChange={(e) => set(i, { minEntrants: num(e.target.value) })} aria-label={`Prize ${i + 1} minimum entrants`} />
            </label>
            {p.kind === "gift_card" ? (
              <div className={c.giftCards} role="group" aria-label={`Prize ${i + 1} gift cards`} data-testid="prize-gift-cards">
                <span className="monoLabel">The winner picks</span>
                {GIFT_CARDS.map((g) => (
                  <label key={g}>
                    <input
                      type="checkbox"
                      checked={p.giftCards.includes(g)}
                      onChange={(e) => set(i, { giftCards: e.target.checked ? GIFT_CARDS.filter((x) => x === g || p.giftCards.includes(x)) : p.giftCards.filter((x) => x !== g) })}
                    />
                    {GIFT_CARD_LABEL[g]}
                  </label>
                ))}
              </div>
            ) : null}
          </li>
        ))}
      </ol>
      <div className={c.rowActions}>
        <Button size="sm" variant="ghost" disabled={value.length >= max} onClick={() => onChange([...value, blankPrize(brackets[0]?.id ?? "")])} data-testid="add-prize">
          + Prize
        </Button>
        <span className={styles.faint}>
          A prize goes out only when its board had at least the minimum entrants; an ineligible winner's prize passes down. Gift cards and other money prizes wait for an admin to confirm the
          results, and need a linked sign-in. Labels are shown in the app: name what the player gets (“$50 gift card”), never “prize”.
        </span>
      </div>
      {problems.length ? (
        <ul className={c.problems} data-testid="prize-problems">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- schedule

/** The window in UTC (the admin's convention), with how it reads in a few time zones. */
export function SchedulePreview({ opensAt, closesAt }: { opensAt: string | null; closesAt: string | null }) {
  if (!opensAt || !closesAt) return null;
  const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zones = [...new Set([local, ...PREVIEW_ZONES])];
  return (
    <div className={c.preview} data-testid="schedule-preview">
      <span className="monoLabel">
        Local times · {hoursBetween(opensAt, closesAt)} h
      </span>
      <table className={c.zones}>
        <tbody>
          {zones.map((z) => (
            <tr key={z}>
              <th scope="row">{z === local ? `${z} (you)` : z}</th>
              <td>{inZone(opensAt, z)}</td>
              <td>→ {inZone(closesAt, z)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- countries

export function CountriesField({ label, hint, value, onChange, testId }: { label: string; hint: string; value: string[]; onChange: (v: string[]) => void; testId: string }) {
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const add = () => {
    const code = draft.trim().toUpperCase();
    if (!code) return;
    if (!isCountry(code)) return setProblem("A 2-letter country code (ISO 3166-1 alpha-2), e.g. US.");
    if (value.includes(code)) return setProblem("Already listed.");
    onChange([...value, code].sort());
    setDraft("");
    setProblem(null);
  };
  return (
    <Field label={label} hint={problem ? <span className={m.required}>{problem}</span> : hint}>
      <div className={c.codes} data-testid={testId}>
        {value.map((code) => (
          <span key={code} className={c.code}>
            {flag(code)} {code}
            <button type="button" aria-label={`Remove ${code}`} onClick={() => onChange(value.filter((x) => x !== code))}>
              ×
            </button>
          </span>
        ))}
        <TextInput
          className={c.narrow}
          value={draft}
          maxLength={2}
          placeholder="US"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          aria-label={`Add to ${label}`}
        />
        <Button size="sm" variant="ghost" onClick={add} disabled={!draft.trim()}>
          Add
        </Button>
      </div>
    </Field>
  );
}

// ---------------------------------------------------------------- cup puzzles

/** Mean ± spread, with the band drawn on 0–1 and the pool's target marked. */
export function Spread({ mean, spread, target }: { mean: number; spread: number; target?: number | null }) {
  const lo = Math.max(0, mean - spread);
  const hi = Math.min(1, mean + spread);
  return (
    <span className={c.spread} title={`mean ${mean.toFixed(3)} · spread ${spread.toFixed(3)}${target != null ? ` · pool target ${target.toFixed(2)}` : ""}`}>
      <span className={c.mono}>
        {mean.toFixed(2)} ± {spread.toFixed(2)}
      </span>
      <span className={c.spreadBar} aria-hidden>
        <span className={c.spreadBand} style={{ left: `${lo * 100}%`, width: `${(hi - lo) * 100}%` }} />
        {target != null ? <span className={c.spreadTarget} style={{ left: `${target * 100}%` }} /> : null}
        <span className={c.spreadMean} style={{ left: `${mean * 100}%` }} />
      </span>
    </span>
  );
}

/** A set's types in play order (ascending difficulty), coloured by category. */
export function TypeChips({ types }: { types: { type: string; category: string; difficulty: number }[] }) {
  return (
    <span className={c.typeChips} data-testid="type-chips">
      {types.map((t, i) => (
        <span key={`${i}:${t.type}`} className={c.typeChip} data-cat={t.category} title={`${i + 1}. ${t.type} · ${t.category} · difficulty ${t.difficulty.toFixed(2)}`}>
          {typeSlug(t.type)}
        </span>
      ))}
    </span>
  );
}
